import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type { TimeRange} from "@@/shared/date-time/time-range";
import { timeRangeZodSchema } from "@@/shared/date-time/time-range";
import type { DurationInMinutes } from "@@/shared/date-time/time-spans";
import {
    discountIdFactory,
    type DiscountId,
} from "@@/shared/discounts/discount-id";
import {
    entityStateZodSchema,
    type EntityState,
} from "@@/shared/entity-status";
import {
    metaContentZodSchema,
    type MetaContent,
} from "@@/shared/meta-content/meta-content";
import { ownerIdZodSchema, type OwnerId } from "@@/shared/owner-id";
import { percentageZodSchema, type Percentage } from "@@/shared/percentage";
import { userIdZodSchema, type UserId } from "@@/shared/users/user-id";
import type { Except } from "type-fest";
import { z } from "zod";
import {
    discountConditionZodSchema,
    type DiscountCondition,
} from "../../conditions/discount-condition";

type TimeStepDiscountType = "TIME_STEP_DISCOUNT";

//Gives discount to the price at the different step values
type TimeStepDiscount = {
    _id: DiscountId;
    _createdAt: IsoAndUnixTimestamp;
    _createdBy: UserId;
    _status: EntityState;
    type: TimeStepDiscountType;
    ownerId: OwnerId;
    validTimeRanges: TimeRange[];
    meta: MetaContent;
    conditions: DiscountCondition[];
    //OverThisTimeThatDiscount
    steps: { step: DurationInMinutes; discount: Percentage }[];
};

type FactoryParams = Except<TimeStepDiscount, "_id" | "_createdAt" | "type">;
const timeStepDiscountFactory = (params: FactoryParams): TimeStepDiscount => {
    return {
        ...params,
        _id: discountIdFactory(),
        _createdAt: nowIsoAndUnix(),
        type: "TIME_STEP_DISCOUNT",
    };
};

const timeStepDiscountZodObject = z.object({
    _id: z.string() as unknown as z.ZodSchema<DiscountId>,
    _createdAt: isoAndUnixZodSchema,
    _createdBy: userIdZodSchema,
    _status: entityStateZodSchema,
    meta: metaContentZodSchema,
    ownerId: ownerIdZodSchema,
    type: z.literal("TIME_STEP_DISCOUNT"),
    validTimeRanges: z.array(timeRangeZodSchema),
    conditions: z.array(discountConditionZodSchema),
    steps: z.array(
        z.object({
            step: z.coerce
                .number()
                .min(1) as unknown as z.ZodSchema<DurationInMinutes>,
            discount: percentageZodSchema,
        }),
    ),
});
const timeStepDiscountZodSchema: z.ZodSchema<TimeStepDiscount> =
    timeStepDiscountZodObject;

export {
    timeStepDiscountFactory,
    timeStepDiscountZodObject,
    timeStepDiscountZodSchema,
};
export type { TimeStepDiscount, TimeStepDiscountType };
